'use client';

import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface TeamMember {
  id: number;
  name: string;
  role: string;
  image: string;
  alt: string;
  bio: string;
  expertise: string[];
  certifications: string[];
  linkedin?: string;
  email?: string;
}

interface LeadershipTeamProps {
  teamMembers: TeamMember[];
}

const LeadershipTeam: React.FC<LeadershipTeamProps> = ({ teamMembers }) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleMemberClick = (member: TeamMember) => {
    if (!isHydrated) return;
    setSelectedMember(member);
  };

  const handleCloseModal = () => {
    if (!isHydrated) return;
    setSelectedMember(null);
  };

  return (
    <section className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-brand-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="UserGroupIcon" size={20} className="text-brand-primary" />
            <span className="text-sm font-cta font-semibold text-brand-primary">Leadership Team</span>
          </div>
          
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
            Meet the Experts Behind Your Success
          </h2>
          
          <p className="font-body text-lg text-muted-foreground leading-relaxed">
            Our leadership team combines decades of technical expertise with a passion for solving complex business challenges through innovative technology solutions.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {teamMembers.map((member) => (
            <div
              key={member.id}
              onClick={() => handleMemberClick(member)}
              className="bg-card rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 overflow-hidden cursor-pointer group"
            >
              <div className="relative h-80 overflow-hidden">
                <AppImage
                  src={member.image}
                  alt={member.alt}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Icon name="ArrowRightIcon" size={20} className="text-white" />
                    <span className="text-sm font-cta font-semibold text-white">View Profile</span>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="font-headline font-bold text-xl text-primary mb-1">
                  {member.name}
                </h3>
                <p className="font-body text-brand-primary font-semibold mb-3">
                  {member.role}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {member.expertise.slice(0, 2).map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-accent/10 text-accent text-xs font-body font-semibold rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                  {member.expertise.length > 2 && (
                    <span className="px-3 py-1 bg-muted text-muted-foreground text-xs font-body font-semibold rounded-full">
                      +{member.expertise.length - 2} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Modal */}
      {isHydrated && selectedMember && (
        <div
          className="fixed inset-0 bg-primary/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleCloseModal}
        >
          <div
            className="bg-card rounded-2xl shadow-elevated max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg transition-colors duration-200 z-10"
                aria-label="Close modal"
              >
                <Icon name="XMarkIcon" size={24} className="text-white" />
              </button>
              
              <div className="relative h-64 lg:h-80 overflow-hidden rounded-t-2xl">
                <AppImage
                  src={selectedMember.image}
                  alt={selectedMember.alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="font-headline font-bold text-3xl text-white mb-2">
                    {selectedMember.name}
                  </h3>
                  <p className="font-body text-xl text-accent font-semibold">
                    {selectedMember.role}
                  </p>
                </div>
              </div>
              
              <div className="p-8">
                <div className="mb-8">
                  <h4 className="font-headline font-bold text-lg text-primary mb-3 flex items-center space-x-2">
                    <Icon name="UserIcon" size={20} className="text-brand-primary" />
                    <span>About</span>
                  </h4>
                  <p className="font-body text-muted-foreground leading-relaxed">
                    {selectedMember.bio}
                  </p>
                </div>
                
                <div className="mb-8">
                  <h4 className="font-headline font-bold text-lg text-primary mb-3 flex items-center space-x-2">
                    <Icon name="AcademicCapIcon" size={20} className="text-brand-primary" />
                    <span>Areas of Expertise</span>
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedMember.expertise.map((skill, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-accent/10 text-accent text-sm font-body font-semibold rounded-lg"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="mb-8">
                  <h4 className="font-headline font-bold text-lg text-primary mb-3 flex items-center space-x-2">
                    <Icon name="ShieldCheckIcon" size={20} className="text-brand-primary" />
                    <span>Certifications</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedMember.certifications.map((cert, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 p-3 bg-muted rounded-lg"
                      >
                        <Icon name="CheckBadgeIcon" size={20} className="text-success flex-shrink-0" variant="solid" />
                        <span className="font-body text-sm text-foreground">{cert}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default LeadershipTeam;